/**
 * wallet-core/account-manager.ts
 *
 * High-level API for managing multiple EOS accounts.
 * All private keys are encrypted before reaching storage.
 *
 * Import flow: random internal password (never shown) encrypts the key; PIN encrypts
 * that internal secret in secure storage (`internal_password_{account}`).
 */

import {
  encryptPrivateKey,
  decryptPrivateKey,
  verifyPassword,
  hashPin,
  pinDerivedPassword,
  generateInternalPasswordHex,
} from './crypto.js'
import { EMBEDDED_WALLET_STORAGE_KEY } from './embedded-constants.js'
import { decryptLegacyEmbeddedBlobV1 } from './legacy-embedded-crypto.js'
import { fetchAccountNameByPublicKey } from './resolve-eos-account.js'
import { loadWalletState, saveWalletState, clearWalletState } from './storage.js'
import { secureGet, secureSet, secureRemove } from './secure-storage.js'
import type { StoredAccount, UnlockedAccount, NetworkId, WalletState } from './types.js'

const PIN_HASH_KEY = 'pin_hash'
const PIN_BINDING_KEY = 'pin_binding_account'
const PIN_FAIL_KEY = 'pin_fail_state'

/** After this many wrong PINs the wallet is wiped (anti offline brute-force). */
const PIN_WIPE_AFTER = 20
/** Start lockout delay after this many consecutive failures. */
const PIN_LOCKOUT_AFTER = 5

type PinFailState = { count: number; lockedUntil: number }

async function readPinFailState(): Promise<PinFailState> {
  try {
    const raw = await secureGet(PIN_FAIL_KEY)
    if (!raw) return { count: 0, lockedUntil: 0 }
    const parsed = JSON.parse(raw) as PinFailState
    return {
      count: typeof parsed.count === 'number' ? parsed.count : 0,
      lockedUntil: typeof parsed.lockedUntil === 'number' ? parsed.lockedUntil : 0,
    }
  } catch {
    return { count: 0, lockedUntil: 0 }
  }
}

async function writePinFailState(state: PinFailState): Promise<void> {
  await secureSet(PIN_FAIL_KEY, JSON.stringify(state))
}

async function clearPinFailState(): Promise<void> {
  await secureRemove(PIN_FAIL_KEY)
}

export function internalPasswordStorageKey(accountName: string): string {
  return `internal_password_${accountName}`
}

function isLegacyEncryption(account: StoredAccount): boolean {
  return account.keyEncryption !== 'internal-random'
}

// ─── Legacy embedded storage (migration) ─────────────────────────────────────

function removeLegacyEmbeddedKeys(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(EMBEDDED_WALLET_STORAGE_KEY)
    localStorage.removeItem('wharfkit.embedded.encryptedKey')
  } catch {
    // ignore
  }
}

export async function migrateEmbeddedPluginData(): Promise<void> {
  if (typeof window === 'undefined') return
  const state = await loadWalletState()
  if (state.accounts.length > 0) {
    removeLegacyEmbeddedKeys()
  }
}

async function publicKeyFromPrivate(privateKey: string): Promise<string> {
  const { PrivateKey } = await import('@wharfkit/antelope')
  return PrivateKey.from(privateKey).toPublic().toString()
}

// ─── AccountManager class ────────────────────────────────────────────────────

export class AccountManager {
  private state: WalletState = { accounts: [], activeAccountName: null }
  private loaded = false
  /** In-memory only until PIN setup or encryptPinForAccount (maps account name → internal hex password). */
  private pendingInternalPasswordByAccount = new Map<string, string>()

  isLoaded(): boolean {
    return this.loaded
  }

  async init(): Promise<void> {
    if (this.loaded) return
    await migrateEmbeddedPluginData()
    this.state = await loadWalletState()
    this.loaded = true
  }

  private ensureLoaded() {
    if (!this.loaded) throw new Error('AccountManager.init() was not called')
  }

  private async persist() {
    await saveWalletState(this.state)
  }

  // ── Read ─────────────────────────────────────────────────────────────────

  getAccounts(): StoredAccount[] {
    this.ensureLoaded()
    return [...this.state.accounts]
  }

  getActiveAccount(): StoredAccount | null {
    this.ensureLoaded()
    const name = this.state.activeAccountName
    return this.state.accounts.find(a => a.name === name) ?? null
  }

  hasAccounts(): boolean {
    this.ensureLoaded()
    return this.state.accounts.length > 0
  }

  async hasPin(): Promise<boolean> {
    const h = await secureGet(PIN_HASH_KEY)
    return h != null && h.length > 0
  }

  /**
   * True if first-time PIN setup still needs the legacy wallet password
   * (accounts encrypted with a user password, no pending internal secret).
   */
  needsLegacyWalletPasswordForPinSetup(): boolean {
    this.ensureLoaded()
    return this.state.accounts.some(a => {
      if (this.pendingInternalPasswordByAccount.has(a.name)) return false
      return isLegacyEncryption(a)
    })
  }

  /**
   * First PIN setup: wraps keys for PIN unlock and stores internal password encrypted by PIN.
   * @param legacyWalletPassword Required only for wallets that still use user-chosen password encryption.
   */
  async setupPin(pin: string, legacyWalletPassword?: string): Promise<void> {
    this.ensureLoaded()
    if (this.state.accounts.length === 0) throw new Error('No accounts')
    const binding = this.state.activeAccountName ?? this.state.accounts[0]!.name

    for (const account of this.state.accounts) {
      let internalPwd = this.pendingInternalPasswordByAccount.get(account.name)
      if (!internalPwd) {
        if (!isLegacyEncryption(account)) {
          throw new Error(`Missing internal password for account ${account.name}`)
        }
        if (!legacyWalletPassword) {
          throw new Error('Wallet password required to finish PIN setup')
        }
        const pk = await decryptPrivateKey(account.encryptedKey, legacyWalletPassword)
        internalPwd = generateInternalPasswordHex()
        account.encryptedKey = await encryptPrivateKey(pk, internalPwd)
        account.keyEncryption = 'internal-random'
      }

      const pk = await decryptPrivateKey(account.encryptedKey, internalPwd)
      account.pinEncryptedKey = await encryptPrivateKey(pk, pinDerivedPassword(pin, account.name))
      await secureSet(
        internalPasswordStorageKey(account.name),
        await encryptPrivateKey(internalPwd, pin),
      )
      this.pendingInternalPasswordByAccount.delete(account.name)
    }

    await this.persist()
    await secureSet(PIN_HASH_KEY, await hashPin(pin, binding))
    await secureSet(PIN_BINDING_KEY, binding)
  }

  async verifyPin(pin: string): Promise<boolean> {
    const fail = await readPinFailState()
    if (fail.lockedUntil > Date.now()) {
      const secs = Math.ceil((fail.lockedUntil - Date.now()) / 1000)
      throw new Error(`Too many PIN attempts. Try again in ${secs}s`)
    }

    const binding = await secureGet(PIN_BINDING_KEY)
    const storedHash = await secureGet(PIN_HASH_KEY)
    if (!binding || !storedHash) return false

    // SECURITY: reserve (persist) this attempt BEFORE verifying the PIN. If the
    // attempt were only counted after a failed check, an attacker could avoid the
    // lockout by killing the process (or forcing a storage error) between the
    // check and the write — a free-retry / fail-open window. Persisting first
    // makes the counter monotonic regardless of how the attempt ends. If the
    // write itself fails, secureSet throws and the unlock is refused (fail-closed).
    const attempt = fail.count + 1
    let lockedUntil = 0
    if (attempt >= PIN_LOCKOUT_AFTER) {
      const delayMs = Math.min(60_000, 2_000 * 2 ** (attempt - PIN_LOCKOUT_AFTER))
      lockedUntil = Date.now() + delayMs
    }
    await writePinFailState({ count: attempt, lockedUntil })

    const h = await hashPin(pin, binding)
    if (h === storedHash) {
      await clearPinFailState()
      return true
    }

    if (attempt >= PIN_WIPE_AFTER) {
      await this.resetWallet()
      await clearPinFailState()
      throw new Error('Wallet wiped after too many wrong PIN attempts')
    }

    return false
  }

  async unlockAccountWithPin(accountName: string, pin: string): Promise<UnlockedAccount> {
    this.ensureLoaded()
    if (!(await this.verifyPin(pin))) {
      throw new Error('Invalid PIN')
    }
    const stored = this.state.accounts.find(a => a.name === accountName)
    if (!stored) throw new Error(`Account "${accountName}" not found`)

    const encInternal = await secureGet(internalPasswordStorageKey(accountName))
    if (encInternal) {
      const internalPwd = await decryptPrivateKey(encInternal, pin)
      const privateKey = await decryptPrivateKey(stored.encryptedKey, internalPwd)
      return {
        name: stored.name,
        privateKey,
        publicKey: stored.publicKey,
        network: stored.network,
      }
    }

    if (stored.pinEncryptedKey) {
      const privateKey = await decryptPrivateKey(
        stored.pinEncryptedKey,
        pinDerivedPassword(pin, stored.name),
      )
      return {
        name: stored.name,
        privateKey,
        publicKey: stored.publicKey,
        network: stored.network,
      }
    }

    throw new Error('PIN not configured for this account')
  }

  async unlockWithPin(pin: string): Promise<UnlockedAccount> {
    const name = this.state.activeAccountName
    if (!name) throw new Error('No active account')
    return this.unlockAccountWithPin(name, pin)
  }

  async clearPin(): Promise<void> {
    this.ensureLoaded()
    if (this.state.accounts.some(a => a.keyEncryption === 'internal-random')) {
      throw new Error(
        'Clearing PIN is not supported for PIN-only wallets. Reset the wallet from settings if needed.',
      )
    }
    for (const a of this.state.accounts) {
      delete a.pinEncryptedKey
    }
    await this.persist()
    await secureRemove(PIN_HASH_KEY)
    await secureRemove(PIN_BINDING_KEY)
  }

  /** After importing a new account while PIN exists (same PIN as existing). */
  async encryptPinForAccount(accountName: string, pin: string): Promise<void> {
    this.ensureLoaded()
    if (!(await this.verifyPin(pin))) throw new Error('Invalid PIN')
    const acc = this.state.accounts.find(a => a.name === accountName)
    if (!acc) throw new Error(`Account "${accountName}" not found`)
    const internalPwd = this.pendingInternalPasswordByAccount.get(accountName)
    if (!internalPwd) throw new Error('Missing internal password for new account')
    const pk = await decryptPrivateKey(acc.encryptedKey, internalPwd)
    acc.pinEncryptedKey = await encryptPrivateKey(pk, pinDerivedPassword(pin, accountName))
    await secureSet(
      internalPasswordStorageKey(accountName),
      await encryptPrivateKey(internalPwd, pin),
    )
    acc.keyEncryption = 'internal-random'
    this.pendingInternalPasswordByAccount.delete(accountName)
    await this.persist()
  }

  // ── Write ─────────────────────────────────────────────────────────────────

  /**
   * @param options.encryptWithPassword — embedded / migration: encrypt with this password instead of random internal.
   */
  async addAccount(
    privateKey: string,
    options?: {
      label?: string
      accountName?: string
      network?: NetworkId
      encryptWithPassword?: string
    },
  ): Promise<StoredAccount> {
    this.ensureLoaded()

    const network = options?.network ?? 'eos-mainnet'

    const { PrivateKey } = await import('@wharfkit/antelope')
    const pk = PrivateKey.from(privateKey.trim())
    const pubLegacy = pk.toPublic().toLegacyString()
    const pubModern = pk.toPublic().toString()
    const publicKeysForResolve =
      pubLegacy === pubModern ? [pubLegacy] : [pubLegacy, pubModern]
    const resolvedName = options?.accountName?.trim()
      ? options.accountName.trim().toLowerCase()
      : await fetchAccountNameByPublicKey(publicKeysForResolve)

    if (this.state.accounts.some(a => a.name === resolvedName && a.network === network)) {
      throw new Error(`Account "${resolvedName}" already exists on ${network}`)
    }

    const legacyPwd = options?.encryptWithPassword
    const encryptionSecret =
      legacyPwd != null && legacyPwd.length > 0 ? legacyPwd : generateInternalPasswordHex()

    const [encryptedKey, publicKey] = await Promise.all([
      encryptPrivateKey(privateKey.trim(), encryptionSecret),
      publicKeyFromPrivate(privateKey.trim()),
    ])

    const labelRaw = options?.label?.trim()
    const label = labelRaw && labelRaw.length > 0 ? labelRaw.slice(0, 64) : undefined

    const keyEncryption: StoredAccount['keyEncryption'] =
      legacyPwd != null && legacyPwd.length > 0 ? 'legacy-password' : 'internal-random'

    const account: StoredAccount = {
      name: resolvedName,
      ...(label !== undefined ? { label } : {}),
      encryptedKey,
      publicKey,
      network,
      createdAt: new Date().toISOString(),
      keyEncryption,
    }

    this.state.accounts.push(account)

    if (keyEncryption === 'internal-random') {
      this.pendingInternalPasswordByAccount.set(resolvedName, encryptionSecret)
    }

    this.state.activeAccountName = resolvedName

    await this.persist()
    return account
  }

  async removeAccount(accountName: string): Promise<void> {
    this.ensureLoaded()
    this.pendingInternalPasswordByAccount.delete(accountName)
    await secureRemove(internalPasswordStorageKey(accountName))
    this.state.accounts = this.state.accounts.filter(a => a.name !== accountName)

    if (this.state.activeAccountName === accountName) {
      this.state.activeAccountName = this.state.accounts[0]?.name ?? null
    }

    if (this.state.accounts.length === 0) {
      await secureRemove(PIN_HASH_KEY)
      await secureRemove(PIN_BINDING_KEY)
    }

    await this.persist()
  }

  async setActiveAccount(accountName: string): Promise<void> {
    this.ensureLoaded()
    if (!this.state.accounts.some(a => a.name === accountName)) {
      throw new Error(`Account "${accountName}" not found`)
    }
    this.state.activeAccountName = accountName
    await this.persist()
  }

  async setAccountAvatar(accountName: string, assetId: string, mediaUrl: string): Promise<void> {
    this.ensureLoaded()
    const acc = this.state.accounts.find(a => a.name === accountName)
    if (!acc) throw new Error(`Account "${accountName}" not found`)
    acc.avatarAssetId = assetId
    acc.avatarUrl = mediaUrl
    await this.persist()
  }

  async unlock(accountName: string, password: string): Promise<UnlockedAccount> {
    this.ensureLoaded()
    const stored = this.state.accounts.find(a => a.name === accountName)
    if (!stored) throw new Error(`Account "${accountName}" not found`)
    if (stored.keyEncryption === 'internal-random') {
      throw new Error('Use PIN to unlock this account')
    }

    const privateKey = await decryptPrivateKey(stored.encryptedKey, password)

    return {
      name: stored.name,
      privateKey,
      publicKey: stored.publicKey,
      network: stored.network,
    }
  }

  async checkPassword(accountName: string, password: string): Promise<boolean> {
    this.ensureLoaded()
    const stored = this.state.accounts.find(a => a.name === accountName)
    if (!stored) return false
    if (stored.keyEncryption === 'internal-random') return false
    return verifyPassword(stored.encryptedKey, password)
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    this.ensureLoaded()

    const decrypted: Array<{ name: string; key: string }> = []
    for (const account of this.state.accounts) {
      if (account.keyEncryption === 'internal-random') {
        throw new Error('Password change is only for legacy password-based accounts')
      }
      const key = await decryptPrivateKey(account.encryptedKey, oldPassword)
      decrypted.push({ name: account.name, key })
    }

    for (const { name, key } of decrypted) {
      const account = this.state.accounts.find(a => a.name === name)!
      account.encryptedKey = await encryptPrivateKey(key, newPassword)
    }

    decrypted.forEach(d => { d.key = '' })

    await this.persist()
  }

  async resetWallet(): Promise<void> {
    for (const a of this.state.accounts) {
      await secureRemove(internalPasswordStorageKey(a.name))
    }
    this.pendingInternalPasswordByAccount.clear()
    this.state = { accounts: [], activeAccountName: null }
    await clearWalletState()
    await secureRemove(PIN_HASH_KEY)
    await secureRemove(PIN_BINDING_KEY)
    await secureRemove(PIN_FAIL_KEY)
  }
}

export const accountManager = new AccountManager()

export async function migrateLegacyEmbeddedWallet(password: string): Promise<void> {
  await accountManager.init()
  const raw = typeof window !== 'undefined' ? localStorage.getItem(EMBEDDED_WALLET_STORAGE_KEY) : null
  if (!raw) return

  const { PrivateKey } = await import('@wharfkit/antelope')
  let plaintext: string
  const trimmed = raw.trimStart()
  if (trimmed.startsWith('pw1.')) {
    plaintext = await decryptPrivateKey(raw, password)
  } else if (trimmed.startsWith('{')) {
    plaintext = await decryptLegacyEmbeddedBlobV1(raw, password)
  } else {
    throw new Error('Unknown embedded wallet format')
  }

  const pk = PrivateKey.from(plaintext)
  const publicKeyStr = pk.toPublic().toLegacyString()
  const accountName = await fetchAccountNameByPublicKey(publicKeyStr)

  if (accountManager.getAccounts().some(a => a.name === accountName)) {
    removeLegacyEmbeddedKeys()
    return
  }

  try {
    await accountManager.addAccount(plaintext, {
      encryptWithPassword: password,
      accountName,
    })
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    if (msg.includes('already exists')) {
      removeLegacyEmbeddedKeys()
      return
    }
    throw e
  }

  removeLegacyEmbeddedKeys()
}
